import { useRef, useState } from 'react';
import { fotoDeArchivo } from '../lib/fotos.js';
import { useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { CamaraFoto, useCamaraEnLaPagina } from './CamaraFoto.js';
import { Ico } from './Iconos.js';
import { Miniatura, type ProductoMiniatura } from './Miniatura.js';

/**
 * Tomar, cambiar o quitar la foto de un producto.
 *
 * Two ways in, a file input each. «Tomar foto» opens the camera itself, the back one, and the
 * photo comes straight back once it is accepted: photographing it on the shelf. It used to be one
 * input with no `capture`, for a choice between the camera and the gallery, and an Android phone
 * opened its gallery instead, a photo taken there and then picked out of it (the owner, 2026-10-20:
 * «on mobile can we make it so that i can just take the photo directly from the camera and it
 * uploads»). «Galería» opens the pictures already on the phone, the one the supplier sent on
 * WhatsApp. A computer ignores `capture`: with a camera, «Tomar foto» opens it in the page
 * (`CamaraFoto`, the same day: «the same type of directly from camera to upload feature on
 * webcam»), and with none, a file, as «Galería» does.
 *
 * The photo is made small and named here, on the device, and stored before anything else
 * happens — so it works with no signal and the product shows it the instant it is saved. What
 * the parent does with the name (an update on an existing product, a field on a new one) is
 * its business.
 */
export function ElegirFoto({
  producto,
  onElegida,
  onQuitada,
}: {
  producto: ProductoMiniatura;
  onElegida: (huella: string) => void | Promise<void>;
  onQuitada?: () => void | Promise<void>;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const camara = useRef<HTMLInputElement>(null);
  const galeria = useRef<HTMLInputElement>(null);
  const enLaPagina = useCamaraEnLaPagina();
  /** The computer's camera, open in the page. */
  const [conCamara, setConCamara] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function alElegir(entrada: HTMLInputElement) {
    const archivo = entrada.files?.[0];
    if (archivo) await usar(archivo);
    // Cleared so choosing the same file again still counts as a change.
    entrada.value = '';
  }

  async function usar(archivo: Blob) {
    setTrabajando(true);
    setError(null);
    try {
      await onElegida(await fotoDeArchivo(archivo));
    } catch (e) {
      // A file the browser cannot decode — a HEIC on a laptop, a PDF picked by mistake. Said
      // plainly; the technical reason goes to the console for whoever is debugging.
      console.warn('[foto]', e);
      setError(t('foto.noSePudo'));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    // Beside the photo where there is room, under it on a narrow phone — rather than a button
    // squeezed until its two words sit on two lines.
    <div className="flex flex-wrap items-center gap-4">
      <Miniatura producto={producto} tamano="xl" />
      <div className="flex min-w-0 flex-1 basis-40 flex-col items-start gap-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secundario px-4 text-sm whitespace-nowrap"
            disabled={trabajando}
            onClick={() => (enLaPagina ? setConCamara(true) : camara.current?.click())}
            data-foto-tomar
          >
            <Ico icono="foto" />
            {t(trabajando ? 'foto.preparando' : producto.foto ? 'foto.cambiar' : 'foto.tomar')}
          </button>
          <button
            type="button"
            className="btn-secundario px-4 text-sm whitespace-nowrap"
            disabled={trabajando}
            onClick={() => galeria.current?.click()}
            data-foto-galeria
          >
            <Ico icono="galeria" />
            {t('foto.galeria')}
          </button>
        </div>
        {producto.foto && onQuitada ? (
          <button
            type="button"
            className="btn-texto gap-1.5 text-sm text-slate-400"
            disabled={trabajando}
            onClick={() => void onQuitada()}
          >
            <Ico icono="quitar" className="size-4" />
            {t('foto.quitar')}
          </button>
        ) : (
          <p className="text-xs text-slate-500">{t('foto.ayuda')}</p>
        )}
        {error && <p className="text-xs text-rose-400">{error}</p>}
      </div>
      <input
        ref={camara}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void alElegir(e.currentTarget)}
      />
      <input ref={galeria} type="file" accept="image/*" className="hidden" onChange={(e) => void alElegir(e.currentTarget)} />
      {conCamara && (
        <CamaraFoto
          // A new product may have no name yet.
          titulo={producto.nombre.trim() ? t('foto.camara.titulo', { producto: nombre(producto) }) : t('foto.tomar')}
          onTomada={(foto) => {
            setConCamara(false);
            void usar(foto);
          }}
          onCerrar={() => setConCamara(false)}
        />
      )}
    </div>
  );
}
